import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { ActionToastContent } from '@/components/action-toast-content';
import {
  APP_MODAL_ROUTES,
  APP_ROUTES,
  ORDER_ROUTES,
} from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_CURRENCY_CODE,
  displayCurrencyPlaceholder,
  formatCurrency,
} from '@/utils/money';
import {
  GET_RESTAURANT_VP_SUBSCRIPTIONS,
  UPSERT_RESTAURANT_SUBSCRIPTION_MUTATION,
} from './subscriptions/queries';
import { getOperationName } from 'apollo-utilities';
import { StackScreenProps } from '@react-navigation/stack';
import {
  SubscriptionForm,
  SubscriptionFormValues,
} from '@/components/subscriptions/subscription-form';
import { Type } from '@/components/toast';
import {
  UpsertRestaurantVp_SubscriptionMutation,
  UpsertRestaurantVp_SubscriptionMutationVariables,
} from '@/types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useMutation } from '@apollo/client';
import { useToasts } from '@/contexts/toast-context';

type Props = StackScreenProps<
  AppModalStackParams,
  APP_MODAL_ROUTES.CreateSubscription
>;

export const CreateSubscriptionScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    params: { metadata, onViewClick, reopenItemMenuOperations },
  } = route;
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { showToast, showErrorToast } = useToasts();
  const [styles] = useStyles();

  const [upsertRestaurantVPSubscription, { loading }] = useMutation<
    UpsertRestaurantVp_SubscriptionMutation,
    UpsertRestaurantVp_SubscriptionMutationVariables
  >(UPSERT_RESTAURANT_SUBSCRIPTION_MUTATION, {
    refetchQueries: [
      getOperationName(GET_RESTAURANT_VP_SUBSCRIPTIONS) as string,
    ],
  });

  const initialValues = {
    frequencyDays: null,
    fulfillmentMethod: null,
    orderDay: null,
    quantity: metadata.minimumQuantity ?? 1,
  };

  const currencyCode = metadata.currencyCode ?? DEFAULT_CURRENCY_CODE;

  const price = metadata.unitPriceInMicros
    ? formatCurrency(metadata.unitPriceInMicros, currencyCode)
    : displayCurrencyPlaceholder(currencyCode);

  const onSubmit = useCallback(
    async (values: SubscriptionFormValues) => {
      // we will have all values after a CTA button disabled check
      const { frequencyDays, fulfillmentMethod, orderDay, quantity } = values;

      Analytics.track('create_subscription.button', {
        quantity,
        variantPackUUID: metadata.variantPackUUID,
      });

      try {
        const newSubscription = await upsertRestaurantVPSubscription({
          variables: {
            frequencyDays: frequencyDays!.value,
            fulfillmentSettingsUUID: fulfillmentMethod!.id,
            orderDay: orderDay!.value,
            quantity,
            restaurantUUID,
            supplierUUID,
            variantPackUUID: metadata.variantPackUUID,
          },
        });
        showToast({
          content: (onClose) => (
            <ActionToastContent
              buttonProps={{
                children: i18n.t('view'),
                onPress: () => {
                  navigation.navigate(APP_ROUTES.Orders, {
                    screen: ORDER_ROUTES.Subscriptions,
                  });
                  onViewClick();
                  onClose();
                },
                style: styles.viewButton,
                textStyle: styles.viewButtonText,
                trackingData: {
                  name: 'subscription_created.view_button',
                  properties: {
                    restaurantUUID,
                    subscriptionUUID:
                      newSubscription.data
                        ?.upsertRestaurantVariantPackSubscription
                        .restaurant_variant_pack_subscription?.uuid,
                    supplierUUID,
                  },
                },
                type: 'tertiary',
              }}
              textContent={i18n.t('subscriptions.created')}
            />
          ),
          showCloseButton: false,
          type: Type.POSITIVE,
        });
        navigation.goBack();
      } catch {
        showErrorToast();
      }
    },
    [
      metadata.variantPackUUID,
      navigation,
      onViewClick,
      restaurantUUID,
      showErrorToast,
      showToast,
      styles,
      supplierUUID,
      upsertRestaurantVPSubscription,
    ],
  );

  const onClose = () => {
    navigation.goBack();
    reopenItemMenuOperations();
  };

  return (
    <SubscriptionForm
      buttonLabel={i18n.t('create_subscription')}
      initialValues={initialValues}
      loading={loading}
      maximumQuantity={metadata.maximumQuantity}
      message={i18n.t('you_can_change_this_later')}
      minimumQuantity={metadata.minimumQuantity}
      onClose={onClose}
      onSubmit={onSubmit}
      price={price}
      title={i18n.t('create_subscription')}
    />
  );
};

const useStyles = createStyles((theme) => ({
  viewButton: {
    paddingHorizontal: 0,
  },
  viewButtonText: {
    color: theme.sk.color.text.ondark,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: theme.fontSizes.medium,
    textDecorationLine: 'underline',
  },
}));
